import { INDIAN_LANGUAGES } from '../constants/indianLanguages';
import { LANGUAGE_PROFICIENCY_LEVELS } from '../constants/languageProficiency';
import { CANDIDATE_SELECT_CLASS, CORPORATE } from '../constants/candidateFormStyles';

function SkillOption({ label, checked, onCheckChange, level, onLevelChange, levelId }) {
  return (
    <div className="inline-flex items-center gap-1.5 shrink-0">
      <label className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--ds-fg))] cursor-pointer font-medium whitespace-nowrap">
        <input
          type="checkbox"
          className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] h-3.5 w-3.5 shrink-0"
          checked={checked}
          onChange={e => onCheckChange(e.target.checked)}
        />
        {label}
      </label>
      {checked && (
        <select
          id={levelId}
          className={`${CANDIDATE_SELECT_CLASS} py-1 pl-2 pr-6 text-[10px] min-w-[6.5rem] max-w-[7.5rem]`}
          value={level}
          onChange={e => onLevelChange(e.target.value)}
        >
          {LANGUAGE_PROFICIENCY_LEVELS.map(opt => (
            <option key={opt.value || 'none'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Languages Known body — use inside a section with CandidateSectionHeader title "Languages Known".
 */
export default function LanguagesKnownSection({ languagesKnown = [], onChange }) {
  const selected = languagesKnown || [];
  const selectedSet = new Set(selected.map(e => e.language.toLowerCase()));
  const available = INDIAN_LANGUAGES.filter(l => !selectedSet.has(l.toLowerCase()));

  const updateEntry = (index, patch) => {
    onChange(selected.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeEntry = index => {
    onChange(selected.filter((_, i) => i !== index));
  };

  const addLanguage = lang => {
    if (!lang) return;
    onChange([
      ...selected,
      { language: lang, read: false, write: false, speak: false, readLevel: '', writeLevel: '', speakLevel: '' }
    ]);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 max-w-md">
        <div className="flex-1 min-w-[11rem]">
          <label className={`${CORPORATE.label} block mb-1`}>Add language</label>
          <select
            className={CANDIDATE_SELECT_CLASS}
            value=""
            onChange={e => {
              const v = e.target.value;
              if (v) addLanguage(v);
              e.target.value = '';
            }}
          >
            <option value="">Choose a language…</option>
            {available.map(lang => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
        {selected.length > 0 && (
          <span className="text-[10px] text-[hsl(var(--ds-muted-fg))] pb-2">{selected.length} language(s)</span>
        )}
      </div>

      {selected.length === 0 ? (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No languages added yet.</p>
      ) : (
        <div className="space-y-2">
          {selected.map((entry, idx) => (
            <div
              key={`${entry.language}-${idx}`}
              className={`${CORPORATE.nestedCard} px-2.5 py-2 flex flex-wrap items-center gap-x-3 gap-y-2`}
            >
              <div className="inline-flex items-center gap-1.5 shrink-0">
                <span className="text-xs font-bold text-[hsl(var(--ds-fg))] whitespace-nowrap">{entry.language}</span>
                <button
                  type="button"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[hsl(var(--ds-muted-fg))] hover:bg-red-50 hover:text-red-700 text-sm font-bold leading-none"
                  onClick={() => removeEntry(idx)}
                  aria-label={`Remove ${entry.language}`}
                  title="Remove"
                >
                  ×
                </button>
              </div>

              <div
                className="inline-flex flex-row flex-wrap items-center gap-x-4 gap-y-1.5 flex-1 min-w-0"
                role="group"
                aria-label={`Proficiency for ${entry.language}`}
              >
                <SkillOption
                  label="Read"
                  checked={!!entry.read}
                  onCheckChange={read => updateEntry(idx, { read, readLevel: read ? entry.readLevel : '' })}
                  level={entry.readLevel || ''}
                  onLevelChange={readLevel => updateEntry(idx, { readLevel })}
                  levelId={`lang-${idx}-read`}
                />
                <SkillOption
                  label="Write"
                  checked={!!entry.write}
                  onCheckChange={write => updateEntry(idx, { write, writeLevel: write ? entry.writeLevel : '' })}
                  level={entry.writeLevel || ''}
                  onLevelChange={writeLevel => updateEntry(idx, { writeLevel })}
                  levelId={`lang-${idx}-write`}
                />
                <SkillOption
                  label="Speak"
                  checked={!!entry.speak}
                  onCheckChange={speak => updateEntry(idx, { speak, speakLevel: speak ? entry.speakLevel : '' })}
                  level={entry.speakLevel || ''}
                  onLevelChange={speakLevel => updateEntry(idx, { speakLevel })}
                  levelId={`lang-${idx}-speak`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
